import { Link } from 'react-router-dom';

import { dateUtils, numberUtils, stringUtils } from '@pukka/common/utils';
import { Badge, Button, Shimmer, Text } from '@pukka/ui-components';

const { pluralize } = stringUtils;
const { formatCurrency } = numberUtils;

interface BillingSummaryProps {
  readonly packageName: string; // Moka Pro, Moka Basic, Moka Enterprise
  readonly description?: string;
  readonly planName?: string; // monthly / yearly
  readonly originalPrice?: number;
  readonly finalPrice?: number;
  readonly nextBillingDate?: string;
  readonly outletCount?: number;
  readonly employeeSlotCount?: number;
  readonly loading?: boolean;
  readonly hideBillingDate?: boolean;
  readonly onClickSeeDetail: () => void;
  readonly onClickModifyBilling?: () => void;
}

function BillingSummary({
  packageName,
  description,
  planName = 'monthly',
  originalPrice = 0,
  finalPrice = 0,
  nextBillingDate,
  outletCount = 0,
  employeeSlotCount = 0,
  loading,
  hideBillingDate,
  onClickSeeDetail,
  onClickModifyBilling,
}: BillingSummaryProps) {
  return (
    <div className="h-full flex-1 rounded-xl bg-neutral-50 px-4 py-5" data-testid="billing-summary">
      <div className="mb-4 grid gap-2">
        <div className="flex items-center justify-between">
          {loading ? (
            <div className="flex flex-col">
              <Shimmer size="lg" className="mb-[3px]" />
              <Shimmer size="sm" />
            </div>
          ) : (
            <div className="flex flex-col">
              <div className="flex h-min items-center gap-1">
                <Text as="h4" variant="title-1" weight="medium">
                  {packageName}
                </Text>
                <Badge text={planName} color="green" type="invert" size="md" className="capitalize" />
              </div>
              {description ? (
                <Text variant="body-2" weight="normal" className="mt-1 text-neutral-600">
                  {description}
                </Text>
              ) : null}
            </div>
          )}
          {loading ? (
            <div>
              <Shimmer size="md" className="w-32" />
              <Shimmer size="sm" className="ml-auto" />
            </div>
          ) : (
            <div className="text-right">
              <Text variant="title-1" className="text-neutral-700">
                Rp {formatCurrency(finalPrice)}
              </Text>
              {originalPrice > finalPrice && (
                <Text variant="caption-2" className="text-red-500 line-through">
                  Rp {formatCurrency(originalPrice)}
                </Text>
              )}
            </div>
          )}
        </div>
        {/* eslint-disable-next-line no-nested-ternary */}
        {loading ? (
          <Shimmer size="md" className="mb-0.5" />
        ) : hideBillingDate ? null : (
          <Text weight="semibold" className="text-neutral-700">
            Next Billing:{' '}
            <Text as="b" weight="bold">
              {dateUtils.formatDate(nextBillingDate)}
            </Text>
          </Text>
        )}
        {loading ? (
          <div>
            <Shimmer size="sm" className="mb-1.5 mt-1 w-40" />
            <Shimmer size="sm" className="w-40" />
          </div>
        ) : (
          <div>
            <Text variant="body-1">
              {pluralize(outletCount, 'Outlet')} (
              <Link to="../outlet">
                <Text as="span" className="cursor-pointer underline" color="blue">
                  click here to see
                </Text>
              </Link>
              )
            </Text>
            {employeeSlotCount > 0 && <Text variant="body-1">{pluralize(employeeSlotCount, 'Additional employee slot')}</Text>}
          </div>
        )}
      </div>
      <div className="flex gap-4">
        <Button className="w-full" disabled={loading} variant="secondary" onClick={onClickSeeDetail}>
          See Detail Payment
        </Button>
        {onClickModifyBilling && (
          <div className="flex w-full flex-col">
            <Button className="w-full" disabled={loading || planName === 'yearly'} onClick={onClickModifyBilling}>
              Modify Billing
            </Button>
            {planName === 'yearly' ? (
              <Text color="neutral" variant="caption-1" className="ml-2 mt-1">
                *
                <Text as="a" href="tel:1500970" color="blue" className="underline">
                  Call 1500970
                </Text>{' '}
                to switch to regular payment
              </Text>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export { BillingSummary };
